import Input from '@/components/forms/Input';
import MultiSelect from '@/components/forms/MultiSelect';
import Select from '@/components/forms/Select';
import { Option } from '@/design-system/multiselect';
import { useAttributes } from '@/hooks/useAttributes';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { DeleteIcon } from '@/icons';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

interface GroupClubsByLocationFieldsProps {
  index: number;
  fieldsLength: number;
  selectedClubs: string[];
  locationOptions: Option[];
  onRemove: (index: number) => void;
}

const GroupClubsByLocationFields = ({
  index,
  fieldsLength,
  selectedClubs,
  onRemove,
  locationOptions,
}: GroupClubsByLocationFieldsProps) => {
  const { register, watch, setValue } = useFormContext();

  const selectedLocationId = watch(`locations.${index}.locationId`) as string;

  // 👇 Watch ALL locations to get all selected locationIds
  const allLocations = watch('locations') as { locationId: string }[];

  const { data: clubsByLocation = [] } = useAttributes('clubByLocation', selectedLocationId);
  const clubOptions = clubsByLocation.map(({ label, labelEng, value }) => ({ label, labelEng, value }));

  // 👇 Collect locationIds selected in OTHER rows (not the current one)
  const otherSelectedLocationIds = allLocations
    .filter((_, i) => i !== index)
    .map((loc) => loc.locationId)
    .filter(Boolean);

  // 👇 Filter out already-selected locations from this row's options
  const filteredLocationOptions = locationOptions.filter((option) => !otherSelectedLocationIds.includes(option.value));
  useEffect(() => {
    setValue(`locations.${index}.totalClub`, String(selectedClubs?.length ?? 0));
  }, [selectedClubs, index, setValue]);
  const { t } = useDynamicLocale();
  return (
    <div>
      {fieldsLength > 1 && (
        <div>
          <button
            type="button"
            className="ml-auto flex gap-2 items-center cursor-pointer"
            onClick={() => onRemove(index)}
          >
            <DeleteIcon size={16} className="text-destructive-text" />
            <Translation id="configurationManagement.groupsManagement.manageSubGroups.delete" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-5 mb-4.5">
        <Select
          label="configurationManagement.groupsManagement.manageSubGroups.location"
          options={filteredLocationOptions}
          placeholder={t('common.form.placeholder.location')}
          name={`locations.${index}.locationId`}
        />
        <MultiSelect
          name={`locations.${index}.clubIds`}
          label="configurationManagement.groupsManagement.manageSubGroups.clubs"
          options={clubOptions}
          className="w-[240px]"
          placeholder={t('common.form.placeholder.hostDisplayOrder')}
          emptyIndicator={<p className="text-center text-sm">No results found</p>}
        />
      </div>

      <div className="flex justify-between items-center">
        <div className="text-control font-medium text-foreground  ">
          <Translation id="configurationManagement.groupsManagement.manageSubGroups.totalClubCounts" />
        </div>
        <div className="w-24">
          <Input label="" className="text-right" {...register(`locations.${index}.totalClub`)} disabled />
        </div>
      </div>

      <hr className="border border-dashed border-secondary mt-4" />
    </div>
  );
};

export default GroupClubsByLocationFields;
